import React from "react";
import { getFirestore, doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { View, Text, StyleSheet, Image, useWindowDimensions, TouchableOpacity, Alert, ActivityIndicator } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import * as ImagePicker from "expo-image-picker";
import { firebaseApp } from "../../firebase/config";
import { uploadSingleImage } from "../../utils/helpers/functions";

const BuildingScreenHeader = ({ name, siteId, buildingImg, address, isAdhoc, cleanName, progress, isAdmin }) => {
  const { width } = useWindowDimensions();
  const normalizedBuildingImage = typeof buildingImg === "string" ? buildingImg.trim() : "";
  const [imageToUse, setImageToUse] = React.useState(normalizedBuildingImage);
  const [uploading, setUploading] = React.useState(false);

  const progressValue = typeof progress === "number" ? progress : 0;
  const isSmallScreen = width < 380;

  const db = getFirestore(firebaseApp);
  const displayBuildingImg = typeof imageToUse === "string" ? imageToUse.trim() : "";

  React.useEffect(() => {
    if (!uploading) {
      setImageToUse(normalizedBuildingImage);
    }
  }, [normalizedBuildingImage, uploading]);

  const onSelectNewImage = async () => {
    if (uploading) {
      return;
    }

    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();

    if (status !== "granted") {
      Alert.alert("Permission needed", "Allow photo library access to update the building image.");
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      quality: 0.8,
    });

    if (!result.canceled && result.assets?.length) {
      const selectedUri = result.assets[0].uri;
      const previousImage = imageToUse;
      setImageToUse(selectedUri);
      await updateBuildingImage(selectedUri, previousImage);
    }
  };

  const updateBuildingImage = async (selectedUri, previousImage) => {
    try {
      setUploading(true);

      if (!siteId) {
        throw new Error("Missing site id for building image update");
      }
      
      const uploadedUrl = await uploadSingleImage(selectedUri);
      
      if (!uploadedUrl) {
        throw new Error("Image upload failed");
      }

      const buildingRef = doc(db, "BuildingsX", siteId, "private_data", "private");
      const buildingSnapshot = await getDoc(buildingRef);
      if (buildingSnapshot.exists()) {
        await updateDoc(buildingRef, { buildingImage: uploadedUrl });
      } else {
        await setDoc(buildingRef, { buildingImage: uploadedUrl });
      }
      setImageToUse(uploadedUrl);
    } catch (error) {
      console.log("Error updating building image:", error);
      setImageToUse(previousImage);
      Alert.alert("Upload failed", "Unable to update the building image. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <View style={styles.headerCard}>
      <View style={styles.labelRow}>
        <View style={styles.badgeContainer}>
          <Ionicons name="build" size={18} color="#1d4ed8" />
          <Text style={styles.badgeText}>{isAdhoc ? "Ad-hoc Site" : "Managed Building"}</Text>
        </View>
        <View style={styles.statusPill}>
          <Text style={styles.statusText}>{cleanName ? "Cleaning Active" : "Idle"}</Text>
        </View>
      </View>

      <View style={[styles.buildingInfo, isSmallScreen && styles.buildingInfoStacked]}>
        <View style={styles.buildingTextContainer}>
          <Text style={styles.buildingName} numberOfLines={2}>
            {name}
          </Text>
          <Text style={styles.buildingAddress} numberOfLines={2}>
            {address || "Address unavailable"}
          </Text>
        </View>

        {/* display or upload the building image */}
        {displayBuildingImg || isAdmin ? (
          <View style={styles.buildingImageWrapper}>
            {displayBuildingImg ? (
              <Image
                source={{ uri: imageToUse }}
                style={[styles.buildingImage, isSmallScreen && styles.buildingImageStacked]}
                resizeMode="cover"
              />
            ) : (
              <TouchableOpacity
                style={[
                  styles.buildingImage,
                  isSmallScreen && styles.buildingImageStacked,
                  styles.emptyImagePlaceholder,
                ]}
                onPress={onSelectNewImage}
                accessibilityRole="button"
                accessibilityLabel="Upload building image"
                disabled={uploading}
              >
                <Ionicons name="image-outline" size={20} color="#6b7280" />
                <Text style={styles.emptyImageText}>{uploading ? "Uploading..." : "Add photo"}</Text>
              </TouchableOpacity>
            )}
            {isAdmin && displayBuildingImg ? (
              <TouchableOpacity
                style={styles.editImageButton}
                onPress={onSelectNewImage}
                accessibilityRole="button"
                accessibilityLabel="Update building image"
                disabled={uploading}
              >
                <Ionicons name="pencil" size={14} color="#ffffff" />
              </TouchableOpacity>
            ) : null}
            {uploading ? (
              <View
                style={[
                  styles.uploadingOverlay,
                  isSmallScreen && styles.uploadingOverlayStacked,
                ]}
                pointerEvents="none"
              >
                <ActivityIndicator size="small" color="#ffffff" />
                <Text style={styles.uploadingText}>Uploading...</Text>
              </View>
            ) : null}
          </View>
        ) : null}
      </View>

      <View style={styles.metaRow}>
        <View style={styles.metaBlock}>
          <Text style={styles.metaLabel}>Progress</Text>
          <Text style={styles.metaValue}>{Math.round(progressValue * 100)}%</Text>
        </View>
        <View style={styles.metaBlock}>
          <Text style={styles.metaLabel}>Next clean</Text>
          <Text style={styles.metaValue}>{cleanName || "None"}</Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  headerCard: {
    width: "100%",
    backgroundColor: "#ffffff",
    borderRadius: 24,
    padding: 20,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    shadowColor: "#0f172a",
    shadowOpacity: 0.08,
    shadowOffset: { width: 0, height: 10 },
    shadowRadius: 25,
    elevation: 3,
  },
  labelRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  badgeContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  badgeText: {
    fontSize: 13,
    color: "#1d4ed8",
    fontWeight: "700",
    marginLeft: 8,
  },
  statusPill: {
    backgroundColor: "#eff6ff",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
  },
  statusText: {
    color: "#1d4ed8",
    fontWeight: "700",
    fontSize: 12,
  },
  buildingInfo: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 18,
  },
  buildingInfoStacked: {
    flexDirection: "column",
  },
  buildingTextContainer: {
    flex: 1,
  },
  buildingName: {
    fontSize: 28,
    fontWeight: "800",
    color: "#111827",
    lineHeight: 36,
    marginBottom: 6,
  },
  buildingAddress: {
    fontSize: 15,
    color: "#4b5563",
    lineHeight: 22,
    marginBottom: 18,
  },
  buildingImage: {
    width: 120,
    height: 120,
    borderRadius: 14,
    backgroundColor: "#e5e7eb",
    marginTop: 4,
  },
  buildingImageStacked: {
    width: "100%",
    height: 180,
    marginTop: 0,
  },
  buildingImageWrapper: {
    position: "relative",
  },
  emptyImagePlaceholder: {
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#d1d5db",
  },
  emptyImageText: {
    marginTop: 6,
    fontSize: 12,
    color: "#6b7280",
    fontWeight: "600",
  },
  editImageButton: {
    position: "absolute",
    right: 8,
    bottom: 8,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "#1d4ed8",
    alignItems: "center",
    justifyContent: "center",
  },
  uploadingOverlay: {
    position: "absolute",
    top: 4,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 14,
    backgroundColor: "rgba(17, 24, 39, 0.45)",
    alignItems: "center",
    justifyContent: "center",
  },
  uploadingOverlayStacked: {
    top: 0,
  },
  uploadingText: {
    marginTop: 8,
    color: "#ffffff",
    fontSize: 12,
    fontWeight: "700",
  },
  metaRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 12,
  },
  metaBlock: {
    flex: 1,
  },
  metaLabel: {
    fontSize: 12,
    color: "#6b7280",
    marginBottom: 4,
  },
  metaValue: {
    fontSize: 16,
    fontWeight: "700",
    color: "#111827",
  },
});

export default React.memo(BuildingScreenHeader);
